import React, { useState } from 'react';
import dayjs from 'dayjs';
import { CustomMonthPicker } from '../../components';


const SampleMonthPickerUsage = () => {
  const [month1, setMonth1] = useState(null);
  const [month2, setMonth2] = useState(dayjs('2024-06-01'));
  const [month3, setMonth3] = useState(null);
  const [month4, setMonth4] = useState(null);
  const [month5, setMonth5] = useState(null);

  return (
    <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '2rem', color: '#1f2937' }}>CustomMonthPicker Usage Examples</h1>

      {/* Basic Usage */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ marginBottom: '1rem', color: '#374151' }}>Basic Usage</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '300px' }}>
          <CustomMonthPicker
            label="Select Month"
            name="basicMonth"
            value={month1}
            onChange={(newValue) => setMonth1(newValue)}
          />
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Selected: {month1 ? month1.format('MMMM YYYY') : 'None'}
          </p>
        </div>
      </section>

      {/* With Default Value */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ marginBottom: '1rem', color: '#374151' }}>With Default Value</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '300px' }}>
          <CustomMonthPicker
            label="Report Month"
            name="defaultMonth"
            defaultValue={dayjs('2024-03-01')}
          />
        </div>
      </section>

      {/* Controlled Value */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ marginBottom: '1rem', color: '#374151' }}>Controlled Value</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '300px' }}>
          <CustomMonthPicker
            label="Billing Month"
            name="controlledMonth"
            value={month2}
            onChange={(newValue) => setMonth2(newValue)}
          />
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Selected: {month2 ? month2.format('MMMM YYYY') : 'None'}
          </p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setMonth2(dayjs())}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Set Current Month
            </button>
            <button
              onClick={() => setMonth2(null)}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
          </div>
        </div>
      </section>

      {/* Required Field */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ marginBottom: '1rem', color: '#374151' }}>Required Field</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '300px' }}>
          <CustomMonthPicker
            label="Start Month"
            name="requiredMonth"
            required
            placeholder="Required field"
          />
        </div>
      </section>

      {/* With Error */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ marginBottom: '1rem', color: '#374151' }}>With Error</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '300px' }}>
          <CustomMonthPicker
            label="Invoice Month"
            name="errorMonth"
            error="Please select a valid month"
            required
          />
        </div>
      </section>

      {/* With Helper Text */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ marginBottom: '1rem', color: '#374151' }}>With Helper Text</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '300px' }}>
          <CustomMonthPicker
            label="Fiscal Month"
            name="helperMonth"
            helperText="Select a month within the fiscal year"
          />
        </div>
      </section>

      {/* Disabled */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ marginBottom: '1rem', color: '#374151' }}>Disabled</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '300px' }}>
          <CustomMonthPicker
            label="Locked Month"
            name="disabledMonth"
            defaultValue={dayjs('2024-01-01')}
            disabled
          />
        </div>
      </section>

      {/* Read Only */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ marginBottom: '1rem', color: '#374151' }}>Read Only</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '300px' }}>
          <CustomMonthPicker
            label="View Only Month"
            name="readOnlyMonth"
            defaultValue={dayjs('2024-05-01')}
            readOnly
          />
        </div>
      </section>

      {/* Different Sizes */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ marginBottom: '1rem', color: '#374151' }}>Different Sizes</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '300px' }}>
          <CustomMonthPicker
            label="Small"
            name="smallMonth"
            size="sm"
            defaultValue={dayjs('2024-02-01')}
          />
          <CustomMonthPicker
            label="Medium (Default)"
            name="mediumMonth"
            size="md"
            defaultValue={dayjs('2024-06-01')}
          />
          <CustomMonthPicker
            label="Large"
            name="largeMonth"
            size="lg"
            defaultValue={dayjs('2024-10-01')}
          />
        </div>
      </section>

      {/* Min and Max Date */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ marginBottom: '1rem', color: '#374151' }}>Min and Max Date</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '300px' }}>
          <CustomMonthPicker
            label="Select Month (2024 Only)"
            name="minMaxMonth"
            value={month3}
            onChange={(newValue) => setMonth3(newValue)}
            minDate={dayjs('2024-01-01')}
            maxDate={dayjs('2024-12-31')}
            helperText="Only months in 2024 are available"
          />
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Selected: {month3 ? month3.format('MMMM YYYY') : 'None'}
          </p>
        </div>
      </section>

      {/* Exclude Specific Months */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ marginBottom: '1rem', color: '#374151' }}>Exclude Specific Months</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '300px' }}>
          <CustomMonthPicker
            label="Available Months"
            name="excludeMonth"
            value={month4}
            onChange={(newValue) => setMonth4(newValue)}
            excludeMonths={[
              dayjs('2024-01-01'),
              dayjs('2024-06-01'),
              dayjs('2024-12-01'),
            ]}
            helperText="January, June, December are excluded"
          />
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Selected: {month4 ? month4.format('MMMM YYYY') : 'None'}
          </p>
        </div>
      </section>

      {/* Include Only Specific Months */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ marginBottom: '1rem', color: '#374151' }}>Include Only Specific Months</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '300px' }}>
          <CustomMonthPicker
            label="Quarter End Months Only"
            name="includeMonth"
            value={month5}
            onChange={(newValue) => setMonth5(newValue)}
            includeMonths={[
              dayjs('2024-03-01'),
              dayjs('2024-06-01'),
              dayjs('2024-09-01'),
              dayjs('2024-12-01'),
            ]}
            helperText="Only Q1-Q4 end months available"
          />
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Selected: {month5 ? month5.format('MMMM YYYY') : 'None'}
          </p>
        </div>
      </section>

      {/* Non-Clearable */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ marginBottom: '1rem', color: '#374151' }}>Non-Clearable</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '300px' }}>
          <CustomMonthPicker
            label="Permanent Selection"
            name="nonClearableMonth"
            defaultValue={dayjs('2024-07-01')}
            isClearable={false}
            helperText="This field cannot be cleared"
          />
        </div>
      </section>

      {/* Custom Placeholder */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ marginBottom: '1rem', color: '#374151' }}>Custom Placeholder</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '300px' }}>
          <CustomMonthPicker
            label="Project Month"
            name="placeholderMonth"
            placeholder="Choose a month..."
          />
        </div>
      </section>

      {/* Practical Examples */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ marginBottom: '1rem', color: '#374151' }}>Practical Examples</h2>

        {/* Report Generation */}
        <div style={{ 
          border: '1px solid #e5e7eb', 
          borderRadius: '8px', 
          padding: '1.5rem', 
          marginBottom: '1.5rem',
          maxWidth: '400px'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937' }}>
            <span className="material-icons" style={{ verticalAlign: 'middle', marginRight: '0.5rem' }}>assessment</span>
            Generate Report
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <CustomMonthPicker
              label="From Month"
              name="reportFromMonth"
              required
            />
            <CustomMonthPicker
              label="To Month"
              name="reportToMonth"
              required
            />
            <button
              style={{
                padding: '0.75rem 1rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
              }}
            >
              <span className="material-icons" style={{ fontSize: '1.25rem' }}>download</span>
              Generate Report
            </button>
          </div>
        </div>

        {/* Subscription Billing */}
        <div style={{ 
          border: '1px solid #e5e7eb', 
          borderRadius: '8px', 
          padding: '1.5rem', 
          marginBottom: '1.5rem',
          maxWidth: '400px',
          backgroundColor: '#f9fafb'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937' }}>
            <span className="material-icons" style={{ verticalAlign: 'middle', marginRight: '0.5rem' }}>credit_card</span>
            Billing Period
          </h3>
          <CustomMonthPicker
            label="Billing Month"
            name="billingMonth"
            defaultValue={dayjs()}
            helperText="Select the billing month for your invoice"
          />
        </div>

        {/* Budget Planning */}
        <div style={{ 
          border: '1px solid #e5e7eb', 
          borderRadius: '8px', 
          padding: '1.5rem', 
          maxWidth: '400px'
        }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937' }}>
            <span className="material-icons" style={{ verticalAlign: 'middle', marginRight: '0.5rem' }}>account_balance_wallet</span>
            Budget Planning
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <CustomMonthPicker
              label="Start"
              name="budgetStart"
              defaultValue={dayjs('2024-01-01')}
              size="sm"
            />
            <CustomMonthPicker
              label="End"
              name="budgetEnd"
              defaultValue={dayjs('2024-12-01')}
              size="sm"
            />
          </div>
          <p style={{ color: '#059669', fontSize: '0.875rem', marginTop: '1rem' }}>
            <span className="material-icons" style={{ fontSize: '1rem', verticalAlign: 'middle', marginRight: '0.25rem' }}>info</span>
            Budget period: 12 months
          </p>
        </div>
      </section>

      {/* All States Summary */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{ marginBottom: '1rem', color: '#374151' }}>All States Summary</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
          <CustomMonthPicker
            label="Normal"
            name="normalState"
            defaultValue={dayjs('2024-04-01')}
          />
          <CustomMonthPicker
            label="Required"
            name="requiredState"
            required
          />
          <CustomMonthPicker
            label="With Error"
            name="errorState"
            error="Invalid selection"
            required
          />
          <CustomMonthPicker
            label="With Helper"
            name="helperState"
            helperText="Select wisely"
          />
          <CustomMonthPicker
            label="Disabled"
            name="disabledState"
            defaultValue={dayjs('2024-08-01')}
            disabled
          />
          <CustomMonthPicker
            label="Read Only"
            name="readOnlyState"
            defaultValue={dayjs('2024-11-01')}
            readOnly
          />
        </div>
      </section>

    </div>
  );
};

export default SampleMonthPickerUsage;